import * as React from 'react';

import { cn } from '@/lib/utils';

type RevealProps = {
  children: React.ReactNode;
  className?: string;
  /** Stagger delay in milliseconds. */
  delay?: number;
  as?: 'div' | 'li' | 'span' | 'section';
};

/**
 * Fades + lifts its children into view the first time they enter the viewport.
 * Falls back to fully visible when reduced motion is requested (handled in CSS).
 */
export function Reveal({
  children,
  className,
  delay = 0,
  as: Tag = 'div',
}: RevealProps) {
  const ref = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    const node = ref.current;
    // Case: keep static content visible if it is already in view, motion is
    // reduced, or the element/observer is unavailable.
    if (
      !node ||
      !('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      node.getBoundingClientRect().top < window.innerHeight
    )
      return;

    node.classList.add('is-pending');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            node.classList.add('is-visible');
            observer.unobserve(node);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
      node.classList.remove('is-pending');
    };
  }, []);

  return (
    <Tag
      ref={ref as React.Ref<never>}
      className={cn('reveal', className)}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </Tag>
  );
}
